import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, type Tunnel, type VpnConfig, type SiteStatus, type EgressState } from '../adminApi'

const STATUS: Record<Tunnel['status'], string> = {
  provisioning: 'готовится',
  active: 'активен',
  error: 'ошибка',
  revoked: 'снят',
}
const fmtDate = (s: string) => new Date(s).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
const fmtDay = (s: string | null) => (s ? new Date(s).toLocaleDateString('ru-RU') : '—')

const SITE_STATE: Record<SiteStatus['state'], { label: string; color: string }> = {
  ok: { label: 'OK', color: 'var(--ok)' },
  blocked: { label: 'бан', color: 'var(--err)' },
  unreachable: { label: 'недоступен', color: 'var(--warn)' },
  error: { label: 'ошибка', color: 'var(--warn)' },
  unknown: { label: '—', color: 'var(--muted)' },
}
function fmtAgo(s: string | null): string {
  if (!s) return '—'
  const sec = Math.max(0, Math.round((Date.now() - new Date(s).getTime()) / 1000))
  if (sec < 60) return `${sec} с назад`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} мин назад`
  return `${Math.round(min / 60)} ч назад`
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function useCopy() {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const copy = (key: string, text: string) =>
    navigator.clipboard.writeText(text).then(() => { setCopiedKey(key); setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500) })
  return { copiedKey, copy }
}

function EgressSwitch() {
  const [st, setSt] = useState<EgressState | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try { setSt(await api.egressMode()) } catch { /* не критично */ }
  }, [])

  useEffect(() => {
    load()
    // poll so "применяется… → применено" обновляется само, когда бокс подхватит
    const id = window.setInterval(load, 15000)
    return () => clearInterval(id)
  }, [load])

  async function pick(mode: string) {
    if (!st || mode === st.mode) return
    setBusy(mode); setErr(null)
    try { setSt(await api.setEgressMode(mode)) }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Ошибка.') }
    finally { setBusy(null) }
  }

  if (!st) return null
  const pending = !st.applied
  return (
    <div className="card panel" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 8 }}>
        <h3 style={{ margin: 0 }}>Выходной IP (рубильник)</h3>
        <span style={{ fontSize: 12, color: pending ? 'var(--warn)' : 'var(--ok)', whiteSpace: 'nowrap' }}>
          {pending
            ? '⏳ применяется на боксе…'
            : `✓ применено${st.appliedAt ? ` · ${fmtAgo(st.appliedAt)}` : ''}`}
        </span>
      </div>
      <p className="muted" style={{ fontSize: 12.5, margin: '0 0 12px' }}>
        Куда уходит весь трафик. Меняется серверно — старые конфиги переключаются сами (бокс подхватывает за ~минуту).
      </p>
      {err && <div className="alert err" style={{ marginBottom: 10 }}>{err}</div>}
      <div style={{ display: 'grid', gap: 8 }}>
        {st.modes.map((m) => {
          const sel = m.mode === st.mode
          return (
            <button
              key={m.mode}
              onClick={() => pick(m.mode)}
              disabled={busy != null}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                padding: '10px 12px', borderRadius: 10, cursor: sel ? 'default' : 'pointer',
                border: `1px solid ${sel ? 'var(--accent, #5b8cff)' : 'var(--line)'}`,
                background: sel ? 'color-mix(in srgb, var(--accent, #5b8cff) 12%, transparent)' : 'transparent',
                color: 'inherit', width: '100%',
              }}
            >
              <span style={{
                width: 16, height: 16, borderRadius: 16, flexShrink: 0,
                border: `5px solid ${sel ? 'var(--accent, #5b8cff)' : 'var(--line)'}`,
                background: sel ? 'var(--accent, #5b8cff)' : 'transparent',
              }} />
              <span style={{ flex: 1 }}>
                <span style={{ fontWeight: 600, fontSize: 14 }}>{m.label}</span>
                <span className="muted" style={{ display: 'block', fontSize: 12 }}>{m.desc}</span>
              </span>
              {busy === m.mode && <span className="spin" />}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function SiteStatusBoard() {
  const [sites, setSites] = useState<SiteStatus[]>([])
  const [stale, setStale] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    try { const r = await api.siteStatus(); setSites(r.sites); setStale(r.stale) }
    catch { /* панель статуса не критична — молча */ }
    finally { setLoaded(true) }
  }, [])

  useEffect(() => {
    load()
    const id = window.setInterval(load, 60000)
    return () => clearInterval(id)
  }, [load])

  if (!loaded) return null
  return (
    <div className="card panel" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8 }}>
        <h3 style={{ margin: 0 }}>Статус площадок</h3>
        {stale && <span style={{ fontSize: 12, color: 'var(--warn)' }}>⚠ данные устарели — чекер не отвечает</span>}
      </div>
      {sites.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>Ещё нет данных. Чекер на боксе шлёт статус каждые несколько минут.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}><table className="tbl">
          <thead>
            <tr className="muted" style={{ textAlign: 'left', fontSize: 11.5 }}>
              <th style={{ padding: '4px 6px', fontWeight: 500 }}>Площадка</th>
              <th className="sm-hide" style={{ padding: '4px 6px', fontWeight: 500 }}>Выход</th>
              <th style={{ padding: '4px 6px', fontWeight: 500 }}>Статус</th>
              <th className="sm-hide" style={{ padding: '4px 6px', fontWeight: 500 }}>HTTP</th>
              <th className="sm-hide" style={{ padding: '4px 6px', fontWeight: 500 }}>Проверено</th>
            </tr>
          </thead>
          <tbody>
            {sites.map((s) => {
              const st = SITE_STATE[s.state] || SITE_STATE.unknown
              return (
                <tr key={s.key} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: '6px' }}>{s.label}</td>
                  <td className="mono muted sm-hide" style={{ padding: '6px' }}>{s.egressIp}</td>
                  <td style={{ padding: '6px', color: st.color, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 8, background: st.color, marginRight: 6, verticalAlign: 'middle' }} />
                    {st.label}
                  </td>
                  <td className="mono muted sm-hide" style={{ padding: '6px' }}>{s.httpCode ?? '—'}</td>
                  <td className="muted sm-hide" style={{ padding: '6px' }}>{fmtAgo(s.checkedAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table></div>
      )}
    </div>
  )
}

export function Tunnels() {
  const [tunnels, setTunnels] = useState<Tunnel[]>([])
  const [configs, setConfigs] = useState<VpnConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [editing, setEditing] = useState<Tunnel | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [ips, setIps] = useState<string[]>([])
  const pollRef = useRef<number | null>(null)

  const load = useCallback(async () => {
    try {
      const { tunnels } = await api.listTunnels()
      setTunnels(tunnels); setError(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось загрузить.')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadConfigs = useCallback(async () => {
    try { const { configs } = await api.listConfigs(); setConfigs(configs) } catch { /* ignore */ }
  }, [])

  useEffect(() => { load(); loadConfigs() }, [load, loadConfigs])
  useEffect(() => { api.listIps().then((r) => setIps(r.ips)).catch(() => {}) }, [])

  async function sync() {
    setSyncing(true); setError(null)
    try {
      await api.syncConfigs()
      // Give the worker a moment to push the snapshot, then refresh.
      await new Promise((r) => setTimeout(r, 4000))
      await loadConfigs()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось синхронизировать.')
    } finally {
      setSyncing(false)
    }
  }

  async function configAction(fn: () => Promise<any>) {
    setError(null)
    try { await fn(); await loadConfigs() } catch (err) { setError(err instanceof ApiError ? err.message : 'Ошибка.') }
  }
  async function downloadConfig(c: VpnConfig) {
    try { downloadText(`svoz-${c.legacyUid || c.id.slice(0, 10)}.conf`, await api.configText(c.id)) }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Не удалось скачать.') }
  }
  async function editComment(c: VpnConfig) {
    const v = prompt('Комментарий для учёта (кому/что это за конфиг):', c.comment || '')
    if (v === null) return
    await configAction(() => api.setConfigComment(c.id, v))
  }

  useEffect(() => {
    const provisioning = tunnels.some((t) => t.status === 'provisioning' || (!t.wg.ready && t.status !== 'revoked'))
    if (provisioning && pollRef.current == null) pollRef.current = window.setInterval(load, 3000)
    else if (!provisioning && pollRef.current != null) { clearInterval(pollRef.current); pollRef.current = null }
    return () => { if (pollRef.current != null) { clearInterval(pollRef.current); pollRef.current = null } }
  }, [tunnels, load])

  async function patchTunnel(fn: () => Promise<any>) {
    setError(null)
    try { await fn(); await load() } catch (err) { setError(err instanceof ApiError ? err.message : 'Ошибка.') }
  }

  const filtered = tunnels.filter((t) => {
    const s = q.trim().toLowerCase()
    if (!s) return true
    return t.label.toLowerCase().includes(s) || (t.comment || '').toLowerCase().includes(s)
  })
  const active = filtered.filter((t) => t.status !== 'revoked')
  const previous = filtered.filter((t) => t.status === 'revoked')

  const cfgFiltered = configs.filter((c) => {
    const s = q.trim().toLowerCase()
    if (!s) return true
    return [c.name, c.username, c.legacyUid, c.clientIP, c.comment].some((v) => (v || '').toLowerCase().includes(s))
  })
  const cfgActive = cfgFiltered.filter((c) => c.status === 'active')

  return (
    <>
      <div className="section-title">
        <div>
          <h2>Туннели</h2>
          <span className="muted">{tunnels.filter((t) => t.status !== 'revoked').length} туннелей · {configs.filter((c) => c.status === 'active').length} ранее выданных конфигов</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={sync} disabled={syncing}>{syncing ? <span className="spin" /> : 'Синхронизировать'}</button>
          <button className="btn primary" onClick={() => setShowNew(true)}>+ Выдать туннель</button>
        </div>
      </div>

      <input className="input" placeholder="Поиск по имени, username, IP или комментарию…" value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 14 }} />

      <EgressSwitch />
      <SiteStatusBoard />

      {error && <div className="alert err">{error}</div>}

      {loading ? (
        <div className="empty"><span className="spin" /></div>
      ) : tunnels.length === 0 && configs.length === 0 ? (
        <div className="card panel empty"><p>Пусто. Выдайте туннель или нажмите «Синхронизировать», чтобы подтянуть ранее выданные конфиги.</p></div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="keys">
              {active.map((t) => (
                <TunnelRow
                  key={t.id}
                  t={t}
                  onEdit={() => setEditing(t)}
                  onRevoke={() => { if (confirm(`Отозвать туннель «${t.label}»? Доступ перестанет работать, запись останется ниже.`)) patchTunnel(() => api.revokeTunnel(t.id)) }}
                  onMakeLink={() => patchTunnel(() => api.makeDownloadLink(t.id))}
                  onMakeSubLink={() => patchTunnel(() => api.makeSubLink(t.id))}
                />
              ))}
            </div>
          )}

          {cfgActive.length > 0 && (
            <>
              <h3 className="prev-title">Ранее выданные <span className="muted">· {cfgActive.length}</span></h3>
              <div className="keys">
                {cfgActive.map((c) => (
                  <ConfigRow
                    key={c.id}
                    c={c}
                    onDownload={() => downloadConfig(c)}
                    onComment={() => editComment(c)}
                    onRevoke={() => { if (confirm(`Отозвать конфиг ${c.name || c.username || c.legacyUid} (${c.clientIP})? Доступ перестанет работать.`)) configAction(() => api.revokeConfig(c.id)) }}
                  />
                ))}
              </div>
            </>
          )}

          {previous.length > 0 && (
            <>
              <h3 className="prev-title">Отозванные туннели <span className="muted">· {previous.length}</span></h3>
              <div className="keys">
                {previous.map((t) => (
                  <PreviousRow
                    key={t.id}
                    t={t}
                    onDelete={() => { if (confirm(`Удалить запись «${t.label}» навсегда?`)) patchTunnel(() => api.deleteTunnel(t.id)) }}
                  />
                ))}
              </div>
            </>
          )}

          {(active.length === 0 && cfgActive.length === 0 && previous.length === 0) && (
            <div className="card panel empty"><p>Ничего не найдено.</p></div>
          )}
        </>
      )}

      {showNew && <TunnelForm ips={ips} onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load() }} />}
      {editing && <TunnelForm tunnel={editing} ips={ips} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load() }} />}
    </>
  )
}

function ConfigRow({ c, onDownload, onComment, onRevoke }: { c: VpnConfig; onDownload: () => void; onComment: () => void; onRevoke: () => void }) {
  const who = c.name || c.username || c.legacyUid || '—'
  return (
    <div className="card keyrow">
      <div className="keymeta">
        <div className="keyname">
          {who}
          {c.profileId === 'admin' && <span className="badge" style={{ color: 'var(--warn)' }}>admin</span>}
          <span className="muted mono" style={{ fontSize: 12.5, fontWeight: 400 }}>{c.clientIP}</span>
          {c.linkedUser && <span className="muted" style={{ fontSize: 11.5 }}>· привязан к юзеру</span>}
        </div>
        {c.username && c.username !== who && <div className="muted" style={{ fontSize: 12.5 }}>@{c.username}</div>}
        {c.comment
          ? <div style={{ fontSize: 13, margin: '2px 0 4px' }}>{c.comment}</div>
          : <div className="muted" style={{ fontSize: 12.5, margin: '2px 0 4px', fontStyle: 'italic' }}>без комментария</div>}
        <div className="muted" style={{ fontSize: 12 }}>выдан {fmtDay(c.issuedAt)}{c.legacyUid ? ` · uid ${c.legacyUid}` : ''}</div>
      </div>
      <div className="actions">
        <button className="btn sm" onClick={onComment}>Коммент</button>
        {c.canDownload && <button className="btn sm" onClick={onDownload}>Скачать</button>}
        <button className="btn sm danger" onClick={onRevoke}>Отозвать</button>
      </div>
    </div>
  )
}

function PreviousRow({ t, onDelete }: { t: Tunnel; onDelete: () => void }) {
  return (
    <div className="card keyrow prevrow">
      <div className="keymeta">
        <div className="keyname">
          {t.label}
          <span className="badge revoked">отозван</span>
        </div>
        {t.comment && <div className="muted" style={{ fontSize: 13, margin: '2px 0 4px' }}>{t.comment}</div>}
        <div className="muted" style={{ fontSize: 12 }}>выдан {fmtDate(t.createdAt)}</div>
      </div>
      <div className="actions">
        <button className="btn sm danger" onClick={onDelete}>Удалить</button>
      </div>
    </div>
  )
}

function TunnelRow({ t, onEdit, onRevoke, onMakeLink, onMakeSubLink }: { t: Tunnel; onEdit: () => void; onRevoke: () => void; onMakeLink: () => void; onMakeSubLink: () => void }) {
  const { copiedKey, copy } = useCopy()
  const limits = [
    `до ${fmtDay(t.expiresAt)}`,
    `${t.deviceLimit} устр.`,
    t.trafficLimitGb > 0 ? `${t.usage.usedGb} / ${t.trafficLimitGb} ГБ` : 'трафик ∞',
  ].join(' · ')
  return (
    <div className="card keyrow">
      <div className="keymeta">
        <div className="keyname">
          {t.label}
          <span className={`badge ${t.status}`}>{STATUS[t.status]}</span>
          <span className="muted mono" style={{ fontSize: 12, fontWeight: 400 }}>IP {t.ip}</span>
        </div>
        {t.comment && <div className="muted" style={{ fontSize: 13, margin: '2px 0 4px' }}>{t.comment}</div>}
        <div className="muted" style={{ fontSize: 12 }}>выдан {fmtDate(t.createdAt)} · {limits}</div>

        {/* Подписка для VPN-приложений — главная ссылка для юзера */}
        <div className="tline">
          <span className="ttag">подписка</span>
          {t.subscription.url ? (
            <>
              <code className="tval">{t.subscription.url}</code>
              <button className="btn sm" onClick={() => copy('sub', t.subscription.url!)}>{copiedKey === 'sub' ? '✓' : 'Копировать'}</button>
              <button className="btn sm" onClick={onMakeSubLink}>Регенерировать</button>
            </>
          ) : (
            <button className="btn sm primary" onClick={onMakeSubLink}>Создать ссылку-подписку</button>
          )}
        </div>

        {/* vless (сырая ссылка — для ручного добавления одного узла) */}
        {t.vless.ready && t.vless.link && (
          <div className="tline">
            <span className="ttag">vless</span>
            <code className="tval">{t.vless.link}</code>
            <button className="btn sm" onClick={() => copy('vless', t.vless.link!)}>{copiedKey === 'vless' ? '✓' : 'Копировать'}</button>
          </div>
        )}

        {/* WG one-time download link */}
        <div className="tline">
          <span className="ttag">WG</span>
          {!t.wg.ready ? (
            <span className="muted" style={{ fontSize: 12.5 }}>конфиг готовится…</span>
          ) : t.download.url ? (
            <>
              <code className="tval">{t.download.used ? 'ссылка использована' : t.download.url}</code>
              {!t.download.used && <button className="btn sm" onClick={() => copy('dl', t.download.url!)}>{copiedKey === 'dl' ? '✓' : 'Копировать'}</button>}
              <button className="btn sm" onClick={onMakeLink}>Регенерировать</button>
            </>
          ) : (
            <button className="btn sm primary" onClick={onMakeLink}>Создать ссылку (1 раз)</button>
          )}
        </div>
      </div>
      <div className="actions">
        <button className="btn sm" onClick={onEdit}>Изменить</button>
        <button className="btn sm danger" onClick={onRevoke}>Отозвать</button>
      </div>
    </div>
  )
}

function TunnelForm({ tunnel, ips, onClose, onDone }: { tunnel?: Tunnel; ips: string[]; onClose: () => void; onDone: () => void }) {
  const editMode = Boolean(tunnel)
  const [label, setLabel] = useState(tunnel?.label || '')
  const [comment, setComment] = useState(tunnel?.comment || '')
  const [ip, setIp] = useState(ips[0] || '')
  const [days, setDays] = useState(30)
  const [devices, setDevices] = useState(2)
  const [trafficGb, setTrafficGb] = useState(0)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!label.trim()) { setErr('Укажите, кому выдан туннель.'); return }
    setBusy(true); setErr(null)
    try {
      if (editMode && tunnel) await api.updateTunnel(tunnel.id, { label: label.trim(), comment: comment.trim() })
      else await api.createTunnel(label.trim(), comment.trim(), ip || ips[0] || '', days, devices, trafficGb)
      onDone()
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Ошибка.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="overlay" onClick={() => !busy && onClose()}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>{editMode ? 'Изменить туннель' : 'Выдать туннель'}</h3>
        {!editMode && <p className="muted" style={{ margin: '0 0 16px', fontSize: 13 }}>Сразу выдаются vless-ссылка и WireGuard-конфиг.</p>}
        {err && <div className="alert err">{err}</div>}
        <div className="field">
          <label>Кому выдан</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Иван / iPhone Маши / ноут" autoFocus maxLength={80} />
        </div>
        {!editMode && ips.length > 1 && (
          <div className="field">
            <label>Сервер (IP)</label>
            <select className="input" value={ip} onChange={(e) => setIp(e.target.value)}>
              {ips.map((x, i) => <option key={x} value={x}>{x}{i === 0 ? ' — основной' : ' — новый'}</option>)}
            </select>
          </div>
        )}
        {!editMode && (
          <>
            <div className="field" style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <label>Срок (дней)</label>
                <input className="input" type="number" min={1} max={3650} value={days} onChange={(e) => setDays(Math.max(1, Number(e.target.value) || 30))} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Устройств</label>
                <input className="input" type="number" min={1} max={20} value={devices} onChange={(e) => setDevices(Math.max(1, Number(e.target.value) || 2))} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Трафик (ГБ)</label>
                <input className="input" type="number" min={0} max={100000} value={trafficGb} onChange={(e) => setTrafficGb(Math.max(0, Number(e.target.value) || 0))} />
              </div>
            </div>
            <p className="muted" style={{ margin: '-6px 0 12px', fontSize: 12 }}>
              Срок, устройства и трафик (0 = безлимит) показываются в VPN-приложении из ссылки-подписки.
            </p>
          </>
        )}
        <div className="field">
          <label>Комментарий <span className="muted">(необязательно)</span></label>
          <textarea className="input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="заметка" rows={2} maxLength={280} style={{ resize: 'vertical' }} />
        </div>
        <div className="modal-actions">
          <button className="btn primary block" type="submit" disabled={busy}>{busy ? <span className="spin" /> : editMode ? 'Сохранить' : 'Выдать'}</button>
          <button className="btn block" type="button" onClick={onClose} disabled={busy}>Отмена</button>
        </div>
      </form>
    </div>
  )
}
